import { useState, useEffect, useRef } from 'react';

interface RateDataPoint {
  date: string;
  value: number;
}

interface RateChartProps {
  data: RateDataPoint[];
  color: string;
}

export function RateChart({ data, color }: RateChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const [dims, setDims] = useState({ w: 300, h: 120 });
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const pad = { t: 6, r: 10, b: 22, l: 38 };
  const count = data.length;
  const lastIdx = count - 1;

  // Measure actual container size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setDims({ w: width, h: height });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleMouse = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const chartW = dims.w - pad.l - pad.r;
    const pct = (mx - pad.l) / chartW;
    const idx = Math.round(Math.max(0, Math.min(lastIdx, pct * lastIdx)));
    setHover(idx);
  };

  const vals = data.map((p) => p.value);
  const minV = Math.min(...vals) - 0.02;
  const maxV = Math.max(...vals) + 0.02;
  const range = maxV - minV || 0.1;

  const vw = dims.w;
  const vh = dims.h;
  const cw = vw - pad.l - pad.r;
  const ch = vh - pad.t - pad.b;
  const toX = (i: number) => pad.l + (i / lastIdx) * cw;
  const toY = (v: number) => pad.t + (1 - (v - minV) / range) * ch;

  const linePts = data
    .map((p, i) => `${toX(i).toFixed(1)},${toY(p.value).toFixed(1)}`)
    .join(' ');
  const areaPts = `${linePts} ${toX(lastIdx).toFixed(1)},${(pad.t + ch).toFixed(1)} ${pad.l},${(pad.t + ch).toFixed(1)}`;
  const gId = `rcg-${data.length}-${color.replace(/[^a-zA-Z0-9]/g, '')}`;
  const ticks = [minV, (minV + maxV) / 2, maxV];

  const midIdx = Math.floor(lastIdx / 2);
  const xLabels = [0, midIdx, lastIdx];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          padding: '0 2px 4px',
          flexShrink: 0,
        }}
      >
        <span
          className="font-mono font-bold text-text-bright"
          style={{ fontSize: '16px' }}
        >
          {hover !== null
            ? data[hover].value.toFixed(2)
            : data[lastIdx].value.toFixed(2)}
          %
        </span>
        <span
          className="font-mono text-text-muted"
          style={{ fontSize: '8px' }}
        >
          {hover !== null ? data[hover].date : `${count}D`}
        </span>
      </div>
      <div ref={containerRef} style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <svg
          ref={svgRef}
          width={vw}
          height={vh}
          style={{
            display: 'block',
            cursor: 'crosshair',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
          onMouseMove={handleMouse}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id={gId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          {/* Grid lines */}
          {ticks.map((t, i) => (
            <line
              key={i}
              x1={pad.l}
              y1={toY(t)}
              x2={vw - pad.r}
              y2={toY(t)}
              stroke="var(--color-panel-border)"
              strokeWidth="0.5"
              strokeDasharray="3,3"
            />
          ))}
          {/* Y-axis labels */}
          {ticks.map((t, i) => (
            <text
              key={`l${i}`}
              x={pad.l - 4}
              y={toY(t) + 3}
              textAnchor="end"
              style={{
                fontSize: '9px',
                fontFamily: 'JetBrains Mono, monospace',
                fill: 'var(--color-text)',
                fontWeight: 500,
              }}
            >
              {t.toFixed(2)}
            </text>
          ))}
          {/* X-axis labels */}
          {xLabels.map((idx) => (
            <text
              key={`x${idx}`}
              x={toX(idx)}
              y={vh - 5}
              textAnchor="middle"
              style={{
                fontSize: '9px',
                fontFamily: 'JetBrains Mono, monospace',
                fill: 'var(--color-text)',
                fontWeight: 500,
              }}
            >
              {data[idx].date}
            </text>
          ))}
          {/* Area + line */}
          <polygon points={areaPts} fill={`url(#${gId})`} />
          <polyline
            points={linePts}
            fill="none"
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ filter: `drop-shadow(0 0 3px ${color}44)` }}
          />
          {/* Hover crosshair */}
          {hover !== null && (
            <>
              <line
                x1={toX(hover)}
                y1={pad.t}
                x2={toX(hover)}
                y2={pad.t + ch}
                stroke={color}
                strokeWidth="0.8"
                opacity="0.5"
                strokeDasharray="3,3"
              />
              <circle
                cx={toX(hover)}
                cy={toY(data[hover].value)}
                r="4"
                fill="var(--color-void)"
                stroke={color}
                strokeWidth="1.5"
              />
              <circle
                cx={toX(hover)}
                cy={toY(data[hover].value)}
                r="1.5"
                fill={color}
              />
            </>
          )}
        </svg>
      </div>
    </div>
  );
}
